import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const faqs = [
    {
        category: "Geralidades & Subscrição",
        questions: [
            {
                question: "O que é IPTV e como funciona?",
                answer: "IPTV (Internet Protocol Television) é uma tecnologia que permite transmitir canais de televisão e conteúdos de vídeo através da sua ligação à internet, sem necessidade de antena de satélite ou cabo tradicional. Com a TV4KLIVE, acede instantaneamente a mais de 79 000 canais e VOD, simplesmente ligando o seu dispositivo (Smart TV, Caixa Android, Smartphone) à internet."
            },
            {
                question: "Quais são os dispositivos compatíveis com TV4KLIVE?",
                answer: "O nosso serviço IPTV é compatível com quase todos os dispositivos conectados: Smart TV (Samsung, LG, Sony, Android TV, etc...), Caixas Android (Nvidia Shield, Xiaomi Mi Box), Fire TV Stick, Apple TV, Smartphones e Tablets (iOS & Android), PC/Mac (via VLC ou Smarters Pro) e caixas MAG."
            },
            {
                question: "Posso usar a minha subscrição em vários ecrãs?",
                answer: "Sim, oferecemos opções multi-ecrãs. Consoante o seu plano, pode ver os seus programas em 1 ou 2 dispositivos simultaneamente. É ideal para partilhar a sua subscrição com a sua família ou amigos."
            },
            {
                question: "Oferecem um teste gratuito antes da compra?",
                answer: "Absolutamente! Estamos convencidos da qualidade do nosso serviço. Pode solicitar um teste gratuito de 24h para verificar a estabilidade, a qualidade de imagem (4K/FHD) e a riqueza do nosso catálogo antes de se comprometer."
            }
        ]
    },
    {
        category: "Instalação & Técnico",
        questions: [
            {
                question: "Como instalar IPTV na minha Smart TV?",
                answer: "A instalação é muito simples. Para Smart TV Samsung ou LG, recomendamos a aplicação 'IPTV Smarters Pro' ou 'IBO Player'. Uma vez instalada a aplicação, basta introduzir as credenciais (Xtream Codes ou link M3U) que enviamos por e-mail após a sua encomenda."
            },
            {
                question: "Que velocidade de internet é necessária para 4K?",
                answer: "Para uma experiência fluida em 4K UHD, recomendamos uma velocidade de ligação estável de pelo menos 25 Mbps. Para Full HD (1080p), 10 Mbps são suficientes. A fibra ótica é ideal, mas uma boa ligação ADSL ou 4G/5G também funciona muito bem."
            },
            {
                question: "O que fazer em caso de cortes ou buffering?",
                answer: "Se encontrar lentidão, verifique primeiro a sua ligação à internet (reinicie o seu router). Certifique-se de usar um cabo Ethernet se possível, em vez de Wi-Fi. Se o problema persistir, o nosso suporte técnico está disponível 7 dias por semana para o ajudar a otimizar as suas configurações."
            },
            {
                question: "Preciso de um VPN para usar TV4KLIVE?",
                answer: "O uso de um VPN não é obrigatório, mas pode ser recomendado se o seu fornecedor de acesso à internet (ISP) limitar a sua ligação durante o streaming. O nosso serviço é compatível com todos os principais VPN do mercado (NordVPN, ExpressVPN, CyberGhost, etc.)."
            }
        ]
    },
    {
        category: "Pagamento & Suporte",
        questions: [
            {
                question: "Quais são os métodos de pagamento aceites?",
                answer: "Aceitamos pagamentos seguros por Cartão Bancário (Visa, Mastercard), PayPal e Criptomoedas (Bitcoin, USDT). Todas as transações são encriptadas e seguras."
            },
            {
                question: "Em quanto tempo recebo os meus acessos?",
                answer: "A entrega é automatizada e quase instantânea. Receberá os seus códigos de acesso e o guia de instalação por e-mail nos 5 a 15 minutos seguintes à validação do seu pagamento."
            },
            {
                question: "Oferecem uma garantia de satisfação ou reembolso?",
                answer: "Sim, oferecemos uma garantia de reembolso de 7 dias. Se encontrar problemas técnicos que o nosso suporte não conseguir resolver, reembolsamos integralmente."
            }
        ]
    }
];

const AccordionItem = ({ question, answer, isOpen, onClick }) => {
    return (
        <motion.div 
            initial={false}
            className="border-b border-white/10 last:border-none"
        >
            <button
                onClick={onClick}
                className="w-full py-6 flex items-center justify-between text-left group"
            >
                <span className={`text-lg font-medium transition-colors duration-300 ${isOpen ? 'text-yellow-400' : 'text-white group-hover:text-yellow-200'}`}>
                    {question}
                </span>
                <span className={`ml-6 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className={`w-6 h-6 ${isOpen ? 'text-yellow-400' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </span>
            </button>
            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                    >
                        <div className="pb-6 text-gray-400 leading-relaxed">
                            {answer}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default function FAQ() {
    const [openIndex, setOpenIndex] = useState("0-0"); // Default open first item

    const toggleAccordion = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <main className="relative overflow-hidden pt-8 pb-20">
            {/* Background Elements */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-yellow-400/5 rounded-full blur-[120px]"></div>
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[120px]"></div>
            </div>

            <div className="container mx-auto px-4 sm:px-6 relative z-10">
                {/* Header */}
                <div className="text-center mb-20">
                    <motion.span 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        className="inline-block py-1 px-3 rounded-full bg-yellow-400/10 border border-yellow-400/20 text-yellow-400 text-sm font-semibold mb-6"
                    >
                        CENTRO DE AJUDA
                    </motion.span>
                    <motion.h1 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-4xl md:text-6xl font-bold text-white mb-6"
                    >
                        Perguntas <span className="text-yellow-400">Frequentes</span>
                    </motion.h1>
                    <motion.p 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-gray-400 text-lg max-w-2xl mx-auto"
                    >
                        Tudo o que precisa de saber sobre a TV4KLIVE. Se não encontrar a resposta à sua pergunta, não hesite em contactar o nosso suporte.
                    </motion.p>
                </div>

                {/* FAQ Sections */}
                <div className="max-w-4xl mx-auto space-y-12">
                    {faqs.map((section, sectionIndex) => (
                        <motion.div 
                            key={sectionIndex}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: sectionIndex * 0.1 }}
                            className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10"
                        >
                            <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-3">
                                <span className="w-2 h-8 bg-yellow-400 rounded-full"></span>
                                {section.category}
                            </h2>
                            <div className="space-y-2">
                                {section.questions.map((item, itemIndex) => {
                                    const index = `${sectionIndex}-${itemIndex}`;
                                    return (
                                        <AccordionItem
                                            key={index}
                                            question={item.question}
                                            answer={item.answer}
                                            isOpen={openIndex === index}
                                            onClick={() => toggleAccordion(index)}
                                        />
                                    );
                                })}
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Still have questions CTA */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="text-center mt-20"
                >
                    <p className="text-gray-400 mb-6">Não encontra a resposta que procura?</p>
                    <a href="https://wa.me/33759073683" target="_blank" rel="noopener noreferrer" className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold px-8 py-3 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-lg">
                        Contactar o Suporte
                    </a>
                </motion.div>
            </div>
        </main>
    );
}
